import { animate, AnimationBuilder, AnimationFactory, AnimationPlayer, style } from '@angular/animations';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IScheduleAvailability } from '@signal/asp-data-commons/lib/interfaces/availability';
import { DateTime } from 'luxon';
import { assetUrl } from '../../../../../../single-spa/asset-url';
import * as fromTeamsSelectors from '../../../../store/team/team.selectors';
import * as fromScheduleDashboardActions from '../../../../store/schedule-dashboard/schedule-dashboard.action';
import * as fromScheduleDashboardSelector from '../../../../store/schedule-dashboard/schedule-dashboard.selectors';
import { ScheduleDashboardState } from '../../../../store/schedule-dashboard/schedule-dashboard.reducer';
import { Store } from '@ngrx/store';
import { Department, IAppointmentSummary, IEmployee, ISummaryByAdvisor, ITeam } from '@signal/asp-data-commons';
import { Observable } from 'rxjs';
import { TeamState } from '../../../../store/team/team.reducer';
import {TranslateService} from '@ngx-translate/core';
import { cacheLimitedStartDate, unsubscribeSubscriptions } from '../../../services/util.service';
import { SubscriptionList } from '../../../models/asp.types';

@Component({
  selector: 'app-appointment-by-week',
  templateUrl: './appointment-by-week.component.html',
  styleUrls: ['./appointment-by-week.component.scss']
})
export class AppointmentByWeekComponent implements OnChanges, OnInit, OnDestroy {

  @Input() dateArray: IScheduleAvailability[];
  @Input() department: string;
  @Input() selectedDate: Date;
  @Input() month: string;
  @Input() viewMode: string;
  @Input() filterValue: any;
  @Input() isTodaySelected: boolean;
  @Output() dateChange: EventEmitter<IScheduleAvailability> = new EventEmitter<IScheduleAvailability>();
  @Output() advisorListChange: EventEmitter<IEmployee[]> = new EventEmitter<IEmployee[]>();
  @Output() dateRangeChange: EventEmitter<IScheduleAvailability> = new EventEmitter<IScheduleAvailability>();
  @Output() loadDateRange:EventEmitter<any> =  new EventEmitter<any>();
  @ViewChild('dateDiv', { static: false }) dateDiv: ElementRef;
  @ViewChild('carousel') carousel: ElementRef;
  currentDate = new Date(new Date().setHours(0, 0, 0, 0));
  isApptLoading$: Observable<boolean>;
  isSlotLoading$: Observable<boolean>;
  teamSlide = 0;
  dateFormat = 'yyyy-MM-dd';
  monthFormat = 'MMMM yyyy';
  currentSlide = 0;
  currentSelection = 0;
  appointmentSummaryForWeek$: Observable<IAppointmentSummary[]>;
  appointmentSummaryForWeekList: IAppointmentSummary[] = [];
  teams$: Observable<ITeam[]>;
  private subscr: SubscriptionList = {};
   currentWeekStartDate:DateTime<boolean> = DateTime.now().startOf('week');
  currentWeekEndDate:DateTime<boolean> = DateTime.now().endOf('week');
  teamList: any[] = [];
  fullCapacityUrl = assetUrl('icons/capacity-indicators/full-capacity.svg');
  halfCapacityUrl = assetUrl('icons/capacity-indicators/half-capacity.svg');
  lowCapacityUrl = assetUrl('icons/capacity-indicators/low-capacity.svg');
  private player: AnimationPlayer;
  isSelectedDateWithinCacheDate:boolean = false;

  constructor(private readonly builder: AnimationBuilder, private teamState: Store<TeamState>, private scheduleDashboardState: Store<ScheduleDashboardState>,
              private readonly translate: TranslateService) {
  }

  ngOnInit() {
    this.isApptLoading$ = this.scheduleDashboardState.select(fromScheduleDashboardSelector.selectScheduleDashboardLoading);
    this.isSlotLoading$ = this.scheduleDashboardState.select(fromScheduleDashboardSelector.selectScheduleDashboardSlotLoading);
    if (this.viewMode === 'Day') {
      this.setToCurrentDate();
    } else if (this.viewMode === 'Week') {
      this.setToCurrentDate();
      this.setUpStore();
      this.initializeSubs();
    }
  }

  setToCurrentDate() {
    let currentDate = this.isTodaySelected ? this.currentDate : this.selectedDate || this.currentDate;
    if(currentDate.getMonth() !== (DateTime.fromFormat(this.month, this.monthFormat).month - 1)){
      currentDate = new Date((DateTime.fromFormat(this.month, this.monthFormat).startOf('month')).toString());
    }
    this.syncSelectionFromDate(currentDate);
    setTimeout(() => {
      this.slideAnimation();
    });
    let event = {} as IScheduleAvailability;
    event = (this.dateArray && this.dateArray.length) ? this.dateArray[this.currentSelection] : {date : new Date()} as IScheduleAvailability;
    this.dateChange.emit(event);
    this.dateRangeChange.emit(event);
    const expectedNextWeek = DateTime.fromISO(this.currentDate.toISOString()).plus({week:+1});
    if(expectedNextWeek.month !== (DateTime.fromFormat(this.month, this.monthFormat).month)){
      this.loadDateRange.emit(expectedNextWeek);
    }
    const expectedPreviousWeek = DateTime.fromISO(this.currentDate.toISOString()).plus({week:-1});
    if(expectedPreviousWeek.month !== (DateTime.fromFormat(this.month, this.monthFormat).month)){
      this.loadDateRange.emit(expectedPreviousWeek);
    }
  }

  setUpStore() {
    let startDate = this.currentWeekStartDate.toFormat(this.dateFormat);
    let endDate = this.currentWeekEndDate.toFormat(this.dateFormat);
    if (this.currentWeekStartDate.month !== DateTime.fromFormat(this.month, this.monthFormat).month) {
      startDate = DateTime.fromFormat(this.month, this.monthFormat).startOf('month').toFormat(this.dateFormat);
      const date = new Date((DateTime.fromFormat(this.month, this.monthFormat).startOf('month')).toString());
      const startDateIndex = this.dateArray.findIndex(x => x.date.getMonth() === date.getMonth() && x.date.getDate() === date.getDate() && x.date.getFullYear() === date.getFullYear());
      this.teamSlide = startDateIndex !== -1 ? startDateIndex : 0;
    }
    if (this.currentWeekEndDate.month !== DateTime.fromFormat(this.month, this.monthFormat).month) {
      endDate = DateTime.fromFormat(this.month, this.monthFormat).endOf('month').toFormat(this.dateFormat);
    }
    const apptPayload = { department: this.department, startDate: startDate, endDate: endDate, filters: this.filterValue, viewMode: this.viewMode };
    this.scheduleDashboardState.dispatch(new fromScheduleDashboardActions.LoadScheduleDashboardAppointments(apptPayload));
    this.appointmentSummaryForWeek$ = this.scheduleDashboardState.select(fromScheduleDashboardSelector.selectScheduleDashboardAppointmentsForWeek, { department: this.department });
    this.teams$ = this.teamState.select(fromTeamsSelectors.selectTeams);
  }

  initializeSubs() {
    this.subscr.teams = this.teams$.subscribe(teamList => {
      this.teamList = [{ department: Department.SERVICE, name: this.translate.instant('serviceSchedule.others'), createdOn: '', dealerCode: '', id: 'OTHER', employeeIds: [] }];
      this.teamList = [...teamList, ...this.teamList];
    });
    this.subscr.appointmentSummaryForWeek = this.appointmentSummaryForWeek$.subscribe(data => {
      this.appointmentSummaryForWeekList = data;
      let allAdvisorsList: IEmployee[] = [];
      this.appointmentSummaryForWeekList.forEach(x=> {
        allAdvisorsList = [...allAdvisorsList, ...x.appointments.map(y=>y.advisor)];
      })
      this.advisorListChange.emit(allAdvisorsList);
    });
  }

  getStatusImage(date: IScheduleAvailability){
    if(date.bookingPercentage > 75 || date.bookingPercentageHrs > 75) {
      return 'url('+this.fullCapacityUrl+')'
    } else if((date.bookingPercentageHrs > 50 && date.bookingPercentageHrs <= 75) || (date.bookingPercentage > 50 && date.bookingPercentage <= 75)){
        return 'url('+this.halfCapacityUrl+')';
    }else if(date.bookingPercentage > 0 || date.bookingPercentageHrs > 0 || date.bookingPercentageHrs === -2 || date.bookingPercentage === -2){
        return 'url('+this.lowCapacityUrl+')';
    }
  }
  
    getBackgroundColor(bookingPercent: number, type: string){
      if(bookingPercent > 0 && bookingPercent <= 50 || bookingPercent === -2 ){
        return '#0A7F2B1A';
      } else if(bookingPercent > 50 && bookingPercent <= 75){
        return '#D878081A';
      } else if(bookingPercent > 75){
        return '#A000001A';
      } else if(bookingPercent === -1 || !bookingPercent){
        return type === 'Hrs' ? 'white' :'#0E131933';
      }
    }
  

  getAdvisorSummary(selection: IScheduleAvailability, team: ITeam) {
    const dateVal = DateTime.fromISO(selection.date.toISOString()).toFormat(this.dateFormat);
    if (this.appointmentSummaryForWeekList.length > 0) {
      const list: ISummaryByAdvisor[] = this.appointmentSummaryForWeekList?.find(x => x.date === dateVal)?.summaryByAdvisor || [];
      if (list.length > 0) {
        let adivosrList = [];
        adivosrList = team.id !== 'OTHER' ? list.filter(x => team.employeeIds?.includes(x.advisorId)) : list.filter(x => this.teamList.findIndex(y => y.employeeIds?.includes(x.advisorId)) === -1);
        return adivosrList;
      }
      return list;
    } else {
      return [];
    }
  }

  isApptAvailable(team: ITeam) {
    let isApptAvailable = false;
    this.appointmentSummaryForWeekList.forEach(appointmentSummary => {
      if (appointmentSummary.summaryByAdvisor.filter(appt => team.employeeIds?.includes(appt.advisorId))?.length > 0 && team.id !== 'OTHER') {
        if ((this.filterValue.teamId && (this.filterValue.teamId === 'ALL' || this.filterValue.teamId === team.id)) || !this.filterValue.teamId) {
          isApptAvailable = true;
        }
      } else if (team.id === 'OTHER' && appointmentSummary.summaryByAdvisor.filter(x => this.teamList.findIndex(y => y.employeeIds?.includes(x.advisorId)) === -1)?.length > 0) {
        if (!this.filterValue.teamId || (this.filterValue.teamId && (this.filterValue.teamId === 'ALL' || this.filterValue.teamId === team.id))) {
          isApptAvailable = true;
        }
      }
    });
    return isApptAvailable;
  }

  getMaxCount(team: ITeam) {
    let maxCount = 0;
    let totalCount = 0;
    this.appointmentSummaryForWeekList.forEach(summary => {
      let advisorList: ISummaryByAdvisor[] = [];
      if (summary.summaryByAdvisor.filter(appt => team.employeeIds?.includes(appt.advisorId))?.length > 0 && team.id !== 'OTHER') {
        advisorList = summary.summaryByAdvisor.filter(appt => team.employeeIds?.includes(appt.advisorId));
      } else if (team.id === 'OTHER' && summary.appointments.filter(x => this.teamList.findIndex(y => y.employeeIds?.includes(x.advisorId)) === -1)?.length > 0) {
        advisorList = summary.summaryByAdvisor.filter(x => this.teamList.findIndex(y => y.employeeIds?.includes(x.advisorId)) === -1);
      }
      if (advisorList.length > 0) {
        maxCount = maxCount < advisorList.length ? advisorList.length : maxCount;
        totalCount = totalCount + advisorList.reduce((sum, current) => sum + current.count, 0);
      }

    });
    return { 'max': maxCount, 'total': totalCount };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.carousel) {
      return;
    }


    if (changes.hasOwnProperty('viewMode') && (changes.viewMode.currentValue !== changes.viewMode.previousValue)) {
      if (this.viewMode === 'Week') {
        this.setToCurrentDate();
        this.setUpStore();
        this.initializeSubs();
      } else if (this.viewMode === 'Day') {
        this.setToCurrentDate();
      }
    }

    if (!changes.hasOwnProperty('selectedDate') && changes.hasOwnProperty('month') && (changes.month.currentValue !== changes.month.previousValue)  ) {
        this.currentSlide = 0;
        const currentDateVal = changes.isTodaySelected?.currentValue ? new Date() : new Date((DateTime.fromFormat(this.month, this.monthFormat).startOf('month')).toString());
        const selectIndex = this.dateArray.findIndex(x => x.date.getDate() === currentDateVal.getDate()
          && x.date.getMonth() === currentDateVal.getMonth()
          && x.date.getFullYear() === currentDateVal.getFullYear());
        this.currentSelection = selectIndex === -1 ? 0 : selectIndex;
        this.teamSlide = 0;
        if (this.viewMode === 'Day') {
          this.dateChange.emit(this.dateArray[this.currentSelection]);
        } else if (this.viewMode === 'Week') {
          const luxonDateTime = DateTime.fromISO(this.dateArray[this.currentSelection].date.toISOString());
          this.dateChange.emit(this.dateArray[this.currentSelection]);
          this.currentWeekStartDate = luxonDateTime;
          this.currentWeekEndDate = luxonDateTime.endOf('week') > luxonDateTime.endOf('month') ? luxonDateTime.endOf('month') : luxonDateTime.endOf('week');
          this.setUpStore();
        }
        setTimeout(() => {
          this.slideAnimation();
        });
    }

    if(( changes.hasOwnProperty('dateArray') && changes.dateArray.currentValue !== changes.dateArray.previousValue)){
      this.syncSelectionFromDate(this.isTodaySelected ? this.currentDate : this.selectedDate || this.currentDate);
      setTimeout(() => {
        this.slideAnimation();
      });
    }

   
    if ((changes.hasOwnProperty('selectedDate') && changes.selectedDate && changes.selectedDate?.currentValue !== '')) {
      this.selectedDate = new Date(this.selectedDate);
      this.syncSelectionFromDate(this.selectedDate);
      if (this.viewMode === 'Day') {
      } else if (this.viewMode === 'Week') {
        const luxonDateTime = DateTime.fromISO(this.dateArray[this.currentSlide].date.toISOString());
        this.currentWeekStartDate = luxonDateTime;
        this.currentWeekEndDate = luxonDateTime.endOf('week') > luxonDateTime.endOf('month') ? luxonDateTime.endOf('month') : luxonDateTime.endOf('week');
        this.setUpStore();
      }
      setTimeout(() => {
        this.slideAnimation();
      });
      if(changes.selectedDate.currentValue < new Date(cacheLimitedStartDate.plus({days:-1}).toFormat(this.dateFormat))){
        this.isSelectedDateWithinCacheDate = true;
      }else{
        this.isSelectedDateWithinCacheDate = false;
      }
    }
  }

  onPrev() {
    if (!this.carousel) {
      return;
    }
    const selectedDateIndex = this.dateArray.findIndex(x => x.date.getDate() === this.selectedDate.getDate()
    && x.date.getMonth() === this.selectedDate.getMonth()
    && x.date.getFullYear() === this.selectedDate.getFullYear());
    this.currentSelection = selectedDateIndex === -1 ? 0 : selectedDateIndex;
    let slideToSub = 0;
    if (this.viewMode === 'Day' || this.viewMode === 'Week') {
      slideToSub = this.currentSelection % 7;
    }
    this.currentSlide = (this.currentSelection - 7) <= 0 ? 0 : ((this.currentSelection - (7 + slideToSub)) + this.dateArray.length) % this.dateArray.length;
    this.teamSlide = this.currentSlide;
    this.currentSelection = this.currentSlide;
    setTimeout(() => {
      this.slideAnimation();
    });
    this.dateChange.emit(this.dateArray[this.currentSlide]);
    const expectedPreviousWeek = DateTime.fromISO(this.dateArray[this.currentSlide].date.toISOString()).plus({week:-1});
    if(expectedPreviousWeek.month !== (DateTime.fromFormat(this.month, this.monthFormat).month)){
      this.loadDateRange.emit(expectedPreviousWeek);
    }
    
   if (this.viewMode === 'Week') {
      const luxonDateTime = DateTime.fromISO(this.dateArray[this.currentSelection].date.toISOString());
      this.currentWeekStartDate = luxonDateTime.startOf('week');
      this.currentWeekEndDate = luxonDateTime.endOf('week');
      this.setUpStore();
    }
  }

  onNext() {
    let slideToAdd = 0;
    const selectedDateIndex = this.dateArray.findIndex(x => x.date.getDate() === this.selectedDate.getDate()
    && x.date.getMonth() === this.selectedDate.getMonth()
    && x.date.getFullYear() === this.selectedDate.getFullYear());
    this.currentSelection = selectedDateIndex === -1 ? 0 : selectedDateIndex;
    if (this.viewMode === 'Day' || this.viewMode === 'Week') {
      slideToAdd = this.currentSelection % 7;
    }
    if ( !this.carousel) {
      return;
    }
    this.currentSlide = (this.currentSelection + (7 - slideToAdd)) % this.dateArray.length;
    this.teamSlide = this.currentSlide;
    setTimeout(() => {
      this.slideAnimation();
    });
   
    this.dateChange.emit(this.dateArray[this.currentSlide]);
    const expectedNextWeek = DateTime.fromISO(this.dateArray[this.currentSlide].date.toISOString()).plus({week:+1});
    if(expectedNextWeek.month !== (DateTime.fromFormat(this.month, this.monthFormat).month)){
      this.loadDateRange.emit(expectedNextWeek);
    }
    if (this.viewMode === 'Week') {
      const luxonDateTime = DateTime.fromISO(this.dateArray[this.currentSlide].date.toISOString());
      this.currentWeekStartDate = luxonDateTime.startOf('week');
      this.currentWeekEndDate = luxonDateTime.endOf('week');
      this.setUpStore();
    }
  }

  private slideAnimation() {
    if (!this.carousel) {
      return;
    }
    const offsetPercent = (this.currentSlide / 7) * -100;
    const transformValue = offsetPercent === 0 ? 'translateX(0%)' : `translateX(${offsetPercent}%)`;
    const myAnimation: AnimationFactory = this.builder.build([
      animate('250ms ease-in', style({ transform: transformValue }))
    ]);

    this.player = myAnimation.create(this.carousel?.nativeElement);
    try {
      this.player.play();
    } catch (e) {
      console.warn('Error when animating: ', e.message);
    }
  }

  getTransformed(date: Date) {
    return date.getDate();
  }

  getDay(date: Date) {
    return DateTime.fromISO(date.toISOString()).toFormat('EEE');
  }

  /* Generated by Copilot- Start */
  private syncSelectionFromDate(selectedDate: Date): void {
    if (!selectedDate || !this.dateArray?.length) {
      this.currentSelection = 0;
      this.currentSlide = 0;
      this.teamSlide = 0;
      return;
    }

    const selectedDateIndex = this.dateArray.findIndex(x => x.date.getDate() === selectedDate.getDate()
      && x.date.getMonth() === selectedDate.getMonth()
      && x.date.getFullYear() === selectedDate.getFullYear());
    const startOfWeek = DateTime.fromISO(selectedDate.toISOString()).startOf('week').toJSDate();
    const selectedDateScrollIndex = this.dateArray.findIndex(x => x.date.getDate() === startOfWeek.getDate()
      && x.date.getMonth() === startOfWeek.getMonth()
      && x.date.getFullYear() === startOfWeek.getFullYear());

    this.currentSelection = selectedDateIndex === -1 ? 0 : selectedDateIndex;
    this.currentSlide = selectedDateScrollIndex === -1 ? 0 : selectedDateScrollIndex;
    this.teamSlide = this.currentSlide;
  }
  /* Generated by Copilot- End */

  selectDate(i: number) {
    if (i === this.currentSelection) {
      return;
    }
    this.currentSelection = i;
    this.dateChange.emit(this.dateArray[this.currentSelection]);
  }

  isDisabled(availability: IScheduleAvailability) {
    if (availability.date.getMonth() !== new Date((DateTime.fromFormat(this.month, this.monthFormat).startOf('month')).toString()).getMonth()) {
      return true;
    }
    return false;
  }

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subscr);
  }

    getTranslation(value){
    return value ? this.translate.instant(`common.${value}`) : value ;
  }
  checkDateWithinCacheLimit(date){
  return date?.date < new Date(cacheLimitedStartDate.plus({days:-1}).toFormat(this.dateFormat))
  }
}
